import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { Dialog, DialogActions, DialogButton, HiddenDialogTitle } from '~/components/ui/Dialog';
import { IconBadge } from '~/components/ui/IconBadge';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { freePlanStore } from '~/lib/stores/pricing/freePlan';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { teamPricingPlansStore } from '~/lib/stores/pricing/teamPlans';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { FILE_SIZE_LIMIT_MB_PERSONAL_TIER } from '~/utils/constants';
import { withSpinner } from '~/utils/withSpinner';

export const DowngradeConfirmationModal = () => {
  const [isConfirming, setIsConfirming] = useState(false);
  const showDowngradeModalData = useStore(pricingStore.showDowngradeModalData);
  const organization = showDowngradeModalData?.organization;

  const planType = showDowngradeModalData?.isPersonal ? 'Pro' : 'Team';

  const FEATURES = [
    <span>
      Losing <span className="text-bolt-ds-textPrimary">all your current tokens</span> or extra-tokens from {planType}
      -only events
    </span>,
    <span>
      Losing <span className="text-bolt-ds-textPrimary">all your extra tokens</span> applied to the {planType} plan
    </span>,
    <span>
      <span className="text-bolt-ds-textPrimary">Hosting shut off</span> for projects with custom domains
    </span>,
    <span className="text-bolt-ds-textPrimary">A 150k tokens (~2 messages) limit per day</span>,
    <span className="text-bolt-ds-textPrimary">File upload limited to {FILE_SIZE_LIMIT_MB_PERSONAL_TIER}MB</span>,
  ];

  const handleClose = () => {
    pricingStore.showDowngradeModalData.set(null);
  };

  const handleCancel = () => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.ConfirmDowngradeToFreePlanCanceled,
      },
    });
    handleClose();
  };

  const confirmDowngrade = () => {
    withSpinner({
      action: async () => {
        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.DowngradeToFreePlanConfirmed,
          },
        });

        if (organization) {
          await teamPricingPlansStore.cancelSubscription();
        } else {
          await freePlanStore.downgradeToFreePlan();
          handleClose();
        }
      },
      onStateUpdate: setIsConfirming,
    });
  };

  return (
    <Dialog
      open={!!showDowngradeModalData}
      onClose={handleClose}
      maxWidth={550}
      closeOnBackdropClick={true}
      showCloseButton={true}
    >
      <HiddenDialogTitle>Downgrade confirmation</HiddenDialogTitle>
      <div className="flex flex-col h-full max-h-[85vh]">
        <div className="p-6 flex-1 overflow-auto">
          <IconBadge type="warning" />
          <h2 className="my-6 text-bolt-ds-textPrimary font-semibold text-lg">Are you sure you want to downgrade?</h2>
          <div className="flex flex-col gap-4 text-bolt-ds-textSecondary">
            <p>
              Downgrading from <b className="text-bolt-ds-textPrimary font-semibold">{planType}</b> to{' '}
              <b className="text-bolt-ds-textPrimary font-semibold">Free</b> plan means:
            </p>
            <div className="flex flex-col gap-4 mb-4">
              {FEATURES.map((feature, idx) => (
                <p key={idx} className="flex items-start gap-2">
                  <span className="i-heroicons:x-mark-solid size-5 mt-0.5" />
                  {feature}
                </p>
              ))}
            </div>
          </div>
        </div>

        <DialogActions className="w-full flex">
          <DialogButton
            disabled={isConfirming}
            loading={isConfirming}
            onClick={confirmDowngrade}
            truncate={false}
            variant="tint"
            className="text-sm h-max flex-1 px-4.5 py-2.5"
            color="warning"
          >
            I'm aware I'll lose my unused tokens and hosting, confirm downgrade
          </DialogButton>
          <DialogButton
            onClick={handleCancel}
            disabled={isConfirming}
            variant="secondary"
            className="text-sm w-[150px] p-7.5"
          >
            Go back
          </DialogButton>
        </DialogActions>
      </div>
    </Dialog>
  );
};
